import S, { DataSignal } from 's-js';
import * as _ from 'underscore';

// services
import objectCreate from 'lib/objectCreate';
import { IAjax, Request } from 'lib/Ajax';
import { AutoSavingDraftObject, DraftAutoSaver } from 'lib/autoSave';
import { callAll } from 'lib/callAll';
import { DraftObject } from 'lib/draftSignals';
import { Entrypoints } from 'core/app/entrypoints';
import { ValidationWatcher, mergeErrors } from 'lib/validationWatcher';
import { TZUtils } from 'lib/tzutils';
import TZDate from 'lib/TZDate';
import UOM from 'lib/UOM';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import uniqueIndex, { UniqueIndex } from 'lib/uniqueIndex';
import { memoize } from 'lib/memoize';
import 'lib/kendo-loader';
import { PCCache } from 'data/PCCache';
import { tzmoment } from 'lib/tzmoment';
import { agentRestrictionErrors } from 'common/services/agentRestrictionsErrorParser';

// models
import { AddCoordinationCardsRequest } from 'data/models/AddCoordinationCardsRequest';
import { CancelPortCallRequest } from 'data/models/CancelPortCallRequest';
import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCardVersion } from 'data/models/CoordinationCardVersion';
import { UserSummary } from 'data/models/UserSummary';
import { PortCall } from 'data/models/PortCall';
import { BerthReservation } from 'data/models/BerthReservation';
import { Issue } from 'data/models/Issue';
import { IssueSeverity } from 'server/enums/Issue';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { TopicCard } from 'data/models/TopicCard';
import { Vessel, VesselData } from 'data/models/Vessel';
import { AddVesselRequest } from 'data/models/AddVesselRequest';
import { PCUser } from 'data/models/PCUser';
import { PhoneNumberKind } from 'server/enums/PhoneNumberKind';
import { PortCallRights } from 'server/enums/Rights';
import { ShareOptions } from 'data/models/Share';
import { ShareType } from 'server/enums/ShareType';
import { ShareScope } from 'server/enums/ShareScope';
import { PortCallProfileResponse, PortCallProfileResponseData, PortCallProfileTopicCard } from 'data/models/PortCallProfile';
import { Measurement } from 'data/models/Measurement';
import { LocalizationContext } from 'common/models/LocalizationContext';
import { EffectiveDate } from 'data/models/ScheduleDate';

// components
import { PortCallLogUpdater, PortCallLogUpdaterParams } from 'portcall/portcalllog/logupdater';
import { LocalVesselSearch, LocalVesselSearchParams } from 'portcall/localvesselsearch/model';
import { PortCallProfileCache } from './cache';
import { PortCallLog, PortCallLogParams } from 'portcall/portcalllog/model';
import { ToggleableMeasurement } from 'portcall/toggleablemeasurement/model';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';
import { DateTimeEditPopup } from 'portcall/datetimeeditpopup/model';
import { NewVessel, NewVesselMode, NewVesselParams} from 'portcall/newvessel/model';
import { TopicCardListItem, TopicCardListItemParams } from 'portcall/topiccardlistitem/model';
import { UserSelector } from 'portcall/userselector/model';
import { WartsilaEnrichmentsSummaryCard, WartsilaEnrichmentsSummaryCardParams } from 'wartsila/wartsilaenrichments/summaryCard';
import {
    CancelledCoordinationCardController,
    CoordinationCardController,
    PortCallSchedule,
    PortCallScheduleParams
} from 'portcall/portcallschedule/model';
import {
    AddCCardsRequestCoordinationSelectionStorageParams,
    CoordinationSelectionStorageFactory,
    SelectCoordinations,
    SelectCoordinationsParams
} from 'portcall/selectcoordinations/model';
import { PCTabStrip, TitledTab } from 'portcall/pctabstrip/model';
import { PortCallReport, PortCallReportParams } from './portcallreport';

export class PortCallProfileParams {
    constructor(
        public readonly id : number,
        public readonly flipToTab : TitledTab | undefined
    ) { }
}

// TH: Specifies display-time precision for toggleable PC and Vessel fields Length, Beam, Air Draft, and Deep Draft
const lengthConversions = [
    {
        uom: UOM.Foot,
        // 12/18/2017 DC TODO
        // TH: To display as feet and inches instead of as decimal feet, uncomment the feetAndInches property and comment out the decimalPlaces property.
        // That requires CSS tweaks to make room for the wider field, so in the interest of getting something in, I'm holding off for now.
        //feetAndInches: true
        decimalPlaces: 2
    },
    {
        uom: UOM.Meter,
        decimalPlaces: 1
    }
];

export const TabItinerary : TitledTab = { Title: 'Itinerary' };
export const TabVessel    : TitledTab = { Title: 'Vessel'    };
export const TabPeople    : TitledTab = { Title: 'People'    };
export const TabLog       : TitledTab = { Title: 'Log'       };

export type PortCallProfile = ReturnType<typeof PortCallProfile>;
export function PortCallProfile(
    user : () => UserSummary | null,
    params : PortCallProfileParams,
    entrypoints : Entrypoints,
    ajax : IAjax,
    data : PCCache,
    cache : PortCallProfileCache,
    tzutils : TZUtils,
    localizationContext : LocalizationContext,
    localVesselSearchModel : (params : LocalVesselSearchParams) => LocalVesselSearch,
    newVesselModel : (params : NewVesselParams) => NewVessel,
    logUpdaterModel : (params : PortCallLogUpdaterParams) => PortCallLogUpdater,
    portCallLogModel : (params : PortCallLogParams) => PortCallLog,
    personPopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    topicCardListItemModel : (params :TopicCardListItemParams) => TopicCardListItem,
    wartsilaEnrichmentsSummaryCardModel : (params : WartsilaEnrichmentsSummaryCardParams) => WartsilaEnrichmentsSummaryCard,
    portcallScheduleModel : (params : PortCallScheduleParams) => PortCallSchedule,
    addCCardsReqCoordSelStorage : (params : AddCCardsRequestCoordinationSelectionStorageParams) => CoordinationSelectionStorageFactory,
    selectCoordinationsModel : (params : SelectCoordinationsParams) => SelectCoordinations,
    portcallReportModel : (params : PortCallReportParams) => PortCallReport
) {
    // initialize model
    const model = cache.PortCallProfileResponses.update1(null, { Id: params.id }),
        portcallModel = data.PortCalls.update1(null, { Id : params.id }); // makes sure that at least a template PortCall is loaded at model.PortCall();

    // send request to update data
    const url = entrypoints.PortCallProfile({ Id: params.id }),
        request = ajax.Url<PortCallProfileResponseData>(url).Get(),
        logUpdater = logUpdaterModel({ portcall: portcallModel });

    request.then(json => {
        cache.PortCallProfileResponses.update1(request.Date, json);
    });

    const toggleableUom = S.value(S.sample(portcallModel.LengthOnlyLength.UOM) ?? UOM.Foot),
        PortCall = PortCallViewModel(portcallModel, ajax, toggleableUom, data);

    const TabStrip = PCTabStrip(
        TabItinerary,
        () => [TabVessel, TabPeople, TabLog],
        params.flipToTab ? { state: 'settled-on-back', tab: params.flipToTab } : { state: 'settled-on-front' }
    );

    // shorthand accessors for common data
    const
        AllIssues = S(() => {
            const
                resIssues = PortCall.ActiveReservations().reduce((all, r) => all.concat(r.Issues()), [] as Issue[]),
                sdIssues = PortCall.Schedule().reduce((all, sd) => all.concat(sd.Issues()), [] as Issue[]);
            return [...resIssues, ...sdIssues];
        }),
        AllConflicts = S(() => AllIssues().filter(i => i.Severity() >= IssueSeverity.Conflict));

    const serverTopicCardIdx = uniqueIndex(model.TopicCards, c => c.Id()),
        topicCardClock = tzutils.clock(1000); // clock for time ago text in topic card display

    // model -> viewmodel memoized maps.  these preserve object identity (1:1 b/w models and viewmodels) so that viewmodels can have state
    const reservationMap = memoize((res : BerthReservation) => ReservationViewModel(res, PortCall, ajax, data)),
        itineraryPointMap = memoize((pt : ItineraryPoint) => ItineraryPointViewModel(pt, ajax)),
        vesselMap = memoize((v : Vessel) => VesselViewModel(v, ajax, PortCall, toggleableUom)),
        topicCardMap = memoize((c : TopicCard) => TopicCardViewModel(c, params.id, ajax, topicCardListItemModel, serverTopicCardIdx, topicCardClock));

    // viewmodels
    const reservations = S(() => PortCall.Reservations().map(reservationMap)),
        vessel = S(() => PortCall.Vessel() && vesselMap(PortCall.Vessel()!)),
        topicCards = PortCall.TopicCards.map(topicCardMap),
        itineraryPts = PortCall.Itinerary.map(itineraryPointMap);
        
    const portcallReport = portcallReportModel({ pc : portcallModel, hideActionsMenu: () => showActionsMenu(false) });

    // TH: We'll probably move the logic for sync'ing ItineraryPoint times to an app endpoint that takes the entire PortCall itinerary.
    // But for 12/31/15, this gives users a way of editing the SEA itinerary point.
    const pilotTime = function (val? : TZDate) {
        const
            _portArrival = () => itineraryPts().find(pt => pt.IsPortArrival()),
            portArrival = val === undefined ? _portArrival() : S.sample(_portArrival);
        if (val === undefined) {
            return portArrival ? portArrival.Departure.Effective() : new TZDate();
        }
        if (portArrival) {
            S.freeze(() => {
                portArrival.Arrival.Effective(val);
                portArrival.Departure.Effective(val);
            });
        }
        return val;
    };

    const hasArrived = () => itineraryPts().some(pt => pt.IsPortArrival() && !!pt.Departure.Actual());

    const
        showPilotTimeEditPopup = S.data(false),
        pilotTimeEditPopup = S(() => showPilotTimeEditPopup() ?
            DateTimeEditPopup({
                value: pilotTime,
                onDismiss: () => showPilotTimeEditPopup(false)
            }) : null
        );

    const berthTimelineUrl = S(function () {
        var url = '#schedulegrid',
            res = PortCall.ActiveReservations()[0],
            pt = res && res.Itinerary();

        if (res && pt) {
            url += '?date=' + tzmoment(pt.Arrival.Effective()).add(-1, 'days').format('YYYY-MM-DD')
                    + '&resid=' + res.Id();
        }

        return url;
    });

    const
        Validation = S(() => mergeErrors(PortCall.Validation(), ...reservations().map(res => res.Validation()), ...itineraryPts().map(pt => pt.Validation()))),
        AgentRestrictions = S(() => agentRestrictionErrors(Validation())),
        ShowAgentRestrictions = S.value(false);

    S(() => ShowAgentRestrictions(Object.keys(AgentRestrictions()).length > 0));

    // modals
    const DeletePortCallModal = DeletePortCallViewModel(ajax, PortCall),
        CancelPortCallModal = CancelPortCallViewModel(ajax, entrypoints, PortCall),
        ReserveByLengthModal = ReserveByLengthViewModel(PortCall),
        ShowExternalVesselSearchModal = S.value(false),
        ExternalVesselSearchModal = S(() =>
            ShowExternalVesselSearchModal() ?
                ExternalVesselSearchViewModel(
                    ajax,
                    entrypoints,
                    data,
                    newVesselModel,
                    PortCall,
                    () => ShowExternalVesselSearchModal(false)
                ) :
                null
        ),
        IssuesModal = IssuesViewModel(AllIssues),
        ShowManageCoordinationsModal = S.value(false),
        ManageCoordinationsModal = S(() => ShowManageCoordinationsModal() ?
            ManageCoordinationsViewModel(
                ajax,
                entrypoints,
                addCCardsReqCoordSelStorage,
                selectCoordinationsModel,
                PortCall,
                () => ShowManageCoordinationsModal(false)
            ) :
            null
        );

    // sub-component view models
    const
        shouldPauseCCardLogUpdates = () => {
            const tabState = TabStrip.State();
            return (
                // Pause C-Card log updates if we're not on the Itinerary tab (our front tab), which is where
                // C-Cards appear.
                tabState.state === 'settled-on-back' ||
                tabState.state === 'flipping-to-back' ||
                // And, like the main PC log (see S computation below), also pause C-Card log updates if a PC
                // deletion is in progress or has succeeded, as we expect querying for log updates for a C-Card
                // belonging to a now-deleted PortCall to return an unauthorized response.
                DeletePortCallModal.ActionInProgressOrSucceeded()
            );
        },
        // For now, the CoordinationCardController and CancelledCoordinationCardController interfaces are so
        // similar that we'll implement both of them together. We can of course split these later as needed.
        scheduleCCardController = (_ccard : CoordinationCard | CoordinationCardVersion) : CoordinationCardController & CancelledCoordinationCardController => {
            return {
                ShouldPauseLogUpdates: shouldPauseCCardLogUpdates
            };
        },
        Schedule = portcallScheduleModel({
            PortCall,
            ccardController: scheduleCCardController,
            cancelledCCardController: scheduleCCardController
        }),
        People = PeopleViewModel(PortCall, model, user, data, ajax, personPopupModel),
        vesselSearch = localVesselSearchModel({ onSelect: v => PortCall.VesselId(v.Id()) }),
        portCallLog = portCallLogModel({ portcall: PortCall });

    const
        showActionsMenu = S.data(false),
        ShowTabStripActionsMenu = S.value(false);

    // Turn log updater on/off on various triggers...
    S(() => {
        // Basic logic -- run log updates as long as we're on the log tab, or we can get a head start if we
        // know we're flipping to the log tab...
        const
            tabState = TabStrip.State(),
            onOrApproachingLogTab =
                tabState.state === 'settled-on-back'  && tabState.tab   === TabLog ||
                tabState.state === 'flipping-to-back' && tabState.toTab === TabLog,
            // ...but also make sure we're not in the process of deleting this PortCall (and haven't already
            // completed that process). That's b/c requesting the log events for a PortCall that doesn't exist
            // will return an unauthorized resp, which would trigger an unneeded login prompt.
            shouldRunLogUpdates = onOrApproachingLogTab && !DeletePortCallModal.ActionInProgressOrSucceeded();

        if (shouldRunLogUpdates) logUpdater.start();
        else logUpdater.stop();
    });

    const extensions = {
        ResEvents: reservations,
        Schedule,
        People,
        TopicCardModels: topicCards.sort((c1, c2) =>
            c1.Model.Title().toLowerCase() < c2.Model.Title().toLowerCase() ? -1
          : c1.Model.Title().toLowerCase() > c2.Model.Title().toLowerCase() ? 1
          : 0
        ),
        Now: () => tzutils.now(),
        ToggleableUomIsFeet: function (val? : boolean) {
            if (arguments.length === 0) return toggleableUom() === UOM.Foot;
            else {
                var uom = val ? UOM.Foot : UOM.Meter;
                toggleableUom(uom);
                return val!;
            }
        },
        TabStrip,
        PortCallLog: portCallLog,
        AllIssues,
        AllConflicts,
        DeletePortCallModal,
        CancelPortCallModal,
        ReserveByLengthModal,
        ShowExternalVesselSearchModal,
        ExternalVesselSearchModal,
        IssuesModal,
        ShowManageCoordinationsModal,
        ManageCoordinationsModal,
        LocalizationContext: localizationContext,
        VesselSearch: vesselSearch,
        PortCall,
        PortCallReport: portcallReport,
        Vessel: vessel,
        DeclarationOfSecurity: () => DOSViewModel(vessel, model.Rights),
        BerthTimelineUrl: berthTimelineUrl,
        PilotTime: pilotTime,
        HasArrived: hasArrived,
        ShowPilotTimeEditPopup: showPilotTimeEditPopup,
        PilotTimeEditPopup: pilotTimeEditPopup,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        ShowTabStripActionsMenu,
        ToggleTabStripActionsMenu,
        TabStripMenuAction,
        AgentRestrictions,
        ShowAgentRestrictions,
        // Combining PJO's IsRequestingFullPageTakeOver & AllowFullPageTakeOver into ShowPortCallReport
        ShowPortCallReport: () =>
            !!portcallReport.DisplayedPortCall(),
        WartsilaEnrichments: wartsilaEnrichmentsSummaryCardModel({
            portcall: portcallModel,
            // Important: Tell the Wartsila Enrichments card that we aren't ready for
            // initial refresh until we've finished our initial data request. That's b/c
            // the PCProfileController reads Issues, but refreshing performs another
            // EnhancedEstimateQuery, which can potentially add/remove Issues. So we hold
            // off on the refresh to avoid a race.
            readyForInitialRefresh: request
        }),
        GoBack: function () {
            history.back();
        }
    };

    return Object.assign(objectCreate(model), extensions);

    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    function ToggleTabStripActionsMenu() {
        ShowTabStripActionsMenu(!S.sample(ShowTabStripActionsMenu));
    }

    function TabStripMenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            ShowTabStripActionsMenu(false);
        });
    }
}

const commonUserTypeaheadOptions = {
    typeaheadOptions: {
        minLength: 0,
        highlight: true,
        hint: false
    },
    matchFn: function (user : PCUserViewModel, query : string) {
        let org = user.Organization();
        return CaseInsensitiveContains(user.Name(), query)
            || (org !== null && CaseInsensitiveContains(org.Name(), query));
    },
    displayKey: function (user : PCUserViewModel) { return user.Name(); }
};

export type PeopleViewModel = ReturnType<typeof PeopleViewModel>;
function PeopleViewModel(
    PortCall: PortCallViewModel,
    model : PortCallProfileResponse,
    user : () => UserSummary | null,
    data : PCCache,
    ajax : IAjax,
    personPopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const SelectedInvitedEditorId = S.data<number | null>(null),
        showUserProfileId = S.data<number | null>(null);

    // user types and groupings
    const pcUserMap = memoize((u : PCUser) => PCUserViewModel(u, PortCall, showUserProfileId, ajax, personPopupModel)),
        userLookup = (userId : number) => { let u = data.PCUsers.index(userId)(); return u && pcUserMap(u); },
        Requestor = S(() => userLookup(PortCall.RequestorId())),
        Agent = S(() => userLookup(PortCall.AgentId())),
        SelectedInvitedEditor = S(() => userLookup(SelectedInvitedEditorId() || 0)),
        // TH: Agency editors are the Agent plus the Agent's "Who Can Edit My PortCalls?" editors.
        // Doing this computation client-side to replace the old server-side computation.
        AgencyEditors = S(function () {
            var ag = Agent();
            if (!ag) return [];
            let pce = ag.PortCallEditors().map(pce => userLookup(pce.PortCallEditorId())!).filter(u => u !== null && u.Active());
            return [ag].concat(pce);
        }),
        SchedulingAuthorityEditors = model.SchedulingAuthorityEditors.map(u => userLookup(u)!).filter(u => u !== null),
        assignableAgents = model.AssignableAgents.map(u => userLookup(u)!).filter(u => u !== null),
        agentAndAssignableAgents = S(function () {
            var ag = Agent();
            if (!ag) return [];
            return [ag].concat(assignableAgents().filter(u => u !== null && u !== ag));
        }),
        InvitedEditors = S(() => PortCall.Sharing() && PortCall.Sharing()!.Shares().filter(s => s.Scope() === ShareScope.User && s.Type() === ShareType.Owner).map(s => s.User()!).filter(u => u !== null).map(pcUserMap) || []),
        // Requestor, Agent, Agent's "Who Can Edit My PortCalls" editors, Berth Mgrs, and Invited Editors
        AllEditors = S(function () {
            var all = (Requestor() ? [Requestor()!] : []).concat(AgencyEditors(), SchedulingAuthorityEditors(), InvitedEditors().map(pcUserMap));
            return Array.from(new Set(all));
        }),
        // Short list of editors shown on the people summary (the one on the main portcall flip, as opposed to the People flip)
        // Show requestor plus agency editors, up to 4 people
        ShortEditorsList = S(() => {
            var _requestor = Requestor(),
                agencyEditorsNoRequestor = AgencyEditors().filter(u => u !== _requestor);
            return (_requestor ? [_requestor] : []).concat(agencyEditorsNoRequestor).slice(0, 4);
        }),
        ShortEditorsListText = S(() => ShortEditorsList().map(u => u.Name()).join(', ')),
        allEditorsIds = S(() => new Set(AllEditors().map(e => e.Id()))),
        invitableEditors = S(() => Array.from(data.PCUsers.items()).filter(u => !allEditorsIds().has(u.Id()) && u.Active()).map(pcUserMap));

    const sharesClient = ajax.Url<ShareOptions>(() => PortCall.Sharing() && PortCall.Sharing()!.SharesUrl() || '', { useCache: true }),
        userShare = S(() => user() && PortCall.Sharing() && PortCall.Sharing()!.Shares().find(s => s.UserId() === user()!.Id()) || null);

    // Typeaheads
    const AgentSelector = UserSelector({
            UserId: PortCall.AgentId,
            Users: agentAndAssignableAgents
        }),
        InvitedEditorSelector = UserSelector({
            UserId: (id? : number) => id ? SelectedInvitedEditorId(id)! : (SelectedInvitedEditorId() || 0), // just converts from S.DataSignal<number | null> to S.DataSignal<number>
            Users: invitableEditors
        });

    return {
        IsWatching : () => userShare() !== null,
        Agent,
        AgentSelector,
        Requestor,
        AgencyEditors,
        SchedulingAuthorityEditors,
        InvitedEditors,
        InvitedEditorSelector,
        AllEditors,
        ShortEditorsList,
        ShortEditorsListText,
        InviteEditor,
        SelectedInvitedEditorId,
        SelectedInvitedEditor,
        WatchPortCall,
    };

    function WatchPortCall() {
        const ushare = userShare();
        if (ushare) {
            var shareClient = ajax.Url(ushare.Url()!, { useCache: true });
            shareClient.Delete();
        } else {
            var sharingToken : ShareOptions = {
                Id: 0,
                Type: ShareType.Viewer,
                Scope: ShareScope.User,
                UserId: user() ? user()!.Id() : 0,
                TokenId: PortCall.SharingId()
            };

            sharesClient.Post(sharingToken);
        }
    }

    function InviteEditor() {
        let editorId = SelectedInvitedEditorId();
        if (editorId == null) return true;

        var share : ShareOptions = {
            Id: 0,
            UserId: editorId,
            Scope: ShareScope.User,
            Type: ShareType.Owner,
            TokenId: PortCall.SharingId()
        };

        sharesClient.Post(share).then(() => {
            SelectedInvitedEditorId(null);
            InvitedEditorSelector.data('');
        });
    }
}

export type PortCallViewModel = ReturnType<typeof PortCallViewModel>;
function PortCallViewModel(
    _portcall : PortCall,
    ajax : IAjax,
    toggleableUom: DataSignal<string>,
    data : PCCache
) {
    const
        client = ajax.Url<PortCall, {}>(() => _portcall.Url()!, { useCache : true }),
        portcallEditables = [
            'AgentId',
            'VesselId',
            'LengthOnlyLength.Num',
            'LengthOnlyLength.UOM',
            'AirDraft.Num',
            'AirDraft.UOM',
            'DeepDraft.Num',
            'DeepDraft.UOM',
            'LastPortOfCall',
            'NextPortOfCall',
            // TH 03/23/2017 -- Unlisted isn't really a data field, since it's computed server-side from UnlistedOverride and Vessel.IsGovernmentVessel.
            // But we'll still include it in our draft object so we can distinguish dirty vs clean state.
            'Unlisted',
            'UnlistedOverride',
            'VoyageNumber'
        ],
        draft = DraftObject(_portcall, portcallEditables),
        portcall = draft.object,
        Validation = ValidationWatcher(client);

    DraftAutoSaver(draft, client);

    portcall.Vessel = S.value(S.sample(_portcall.Vessel));
    data.PortCalls.attach(portcall, [portcall.Vessel]);

    // TH 03/23/2017 -- Update our draft Unlisted status when UnlistedOverride or Vessel are edited.
    // Note that this is just for the purposes of setting the client-side value.
    // The server does its own computation of PortCall.Unlisted.
    S.on(callAll(portcall.UnlistedOverride, portcall.Vessel), () => {
        // TH 03/23/2017 -- Same idea here as for the portcall Length computation above.
        // PortCall.Unlisted and Vessel.IsGovernmentVessel can get out of sync (since our server-side
        // code intentionally limits the scope of PortCalls that can be affected by Vessel IsGovernmentVessel edits).
        // So we wanna distinguish initial data population from true UnlistedOverride or Vessel edits.
        if (portcall.UnlistedOverride() === _portcall.UnlistedOverride() && portcall.Vessel() === _portcall.Vessel()) return;

        portcall.Unlisted(
            portcall.UnlistedOverride() !== null ? portcall.UnlistedOverride()! :
            portcall.Vessel()           !== null ? portcall.Vessel()!.IsGovernmentVessel() :
            false
        );
    }, null, true);

    // Make sure to clear any pre-existing LengthOnlyLength when selecting a vessel, whether via local search,
    // external search, etc. LengthOnlyLength and VesselId are mutually exclusive (the server has a validation
    // rule to enforce this).
    S.on(portcall.VesselId, () => {
        if (portcall.VesselId() !== null) portcall.LengthOnlyLength.Num(null);
    }, undefined, true);

    return Object.assign(portcall, {
        IsCaching: () => draft.methods.isCaching(),
        ToggleableVesselLength: ToggleableMeasurement({ backingMeasurement: portcall.VesselLength, uom: toggleableUom, conversions: lengthConversions }),
        ToggleableAirDraft: ToggleableMeasurement({ backingMeasurement: portcall.AirDraft, uom: toggleableUom, conversions: lengthConversions }),
        ToggleableDeepDraft: ToggleableMeasurement({ backingMeasurement: portcall.DeepDraft, uom: toggleableUom, conversions: lengthConversions }),
        ToggleUnlistedOverride: () => portcall.Unlisted() ? portcall.UnlistedOverride(false) : portcall.UnlistedOverride(true),
        Validation
    });
}

export type PCUserViewModel = ReturnType<typeof PCUserViewModel>;
function PCUserViewModel(
    pcUser : PCUser,
    PortCall : PortCallViewModel,
    showUserProfileId: DataSignal<number | null>,
    ajax : IAjax,
    personPopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const phoneNumbers = pcUser.PhoneNumbers,
        // divide phone numbers into a main number and other numbers
        // main number is:
        // 1) first mobile number, if user has any mobile numbers
        // 2) otherwise, first number of any type, if user has any numbers at all
        // 3) otherwise, null
        mobileNumbers = () => phoneNumbers().filter(pn => pn.Kind() === PhoneNumberKind.Mobile),
        mainPhoneNumber = () => {
            var _mobileNumbers = mobileNumbers(),
                allNumbers = phoneNumbers();
            return _mobileNumbers.length > 0 ? _mobileNumbers[0]
                    : allNumbers.length > 0 ? allNumbers[0]
                    : null;
        },
        showProfile = () => showUserProfileId() === pcUser.Id();

    return Object.assign(objectCreate(pcUser), {
        MobileNumbers: mobileNumbers,
        MainPhoneNumber: mainPhoneNumber,
        Name: () => pcUser.FullName(),
        ShowProfile: showProfile,
        ProfilePopup: S(() => showProfile() ? personPopupModel({ id : pcUser.Id(), onHidePopup: () => showUserProfileId(0) }) : null),
        ToggleProfile: ToggleProfile,
        DeleteInvitedEditorShare
    });

    function ToggleProfile() {
        showUserProfileId(S.sample(showProfile) ? null : pcUser.Id());
    }

    function DeleteInvitedEditorShare() {
        var share = pcUser.Shares().find(s => s.TokenId() === (PortCall.Sharing() ? PortCall.Sharing()!.Id() : 0));
        if (!share) return;
        ajax.Url(() => share!.Url()!, { useCache: true }).Delete();
    }
}

// modals
export type DeletePortCallViewModel = ReturnType<typeof DeletePortCallViewModel>;
function DeletePortCallViewModel(ajax: IAjax, portcall: PortCall) {
    var Show = S.data(false),
        client = ajax.Url<PortCall>(() => portcall.Url()!, { useCache: true }),
        Action = S.data<Request<PortCall> | null>(null),
        ActionSuccess = S(function () {
            return Action() && Action()!.Response() && Action()!.Response()!.Success;
        }),
        ActionInProgressOrSucceeded = () => !!Action() && (!Action()!.Response() || Action()!.Response()!.Success);

    return {
        Show,
        Action,
        ActionSuccess,
        ActionInProgressOrSucceeded,
        Delete,
        DoneDeleting
    };

    function Delete() {
        Action(client.Delete());
    }

    function DoneDeleting() {
        Show(false);
        // need to use a timeout otherwise modal hide animation doesn't finish
        // and semi-transparent modal backdrop is stuck on screen
        setTimeout(function () {
            window.location.hash = '#!schedulegrid';
        }, 200);
    }
}

export type CancelPortCallViewModel = ReturnType<typeof CancelPortCallViewModel>;
function CancelPortCallViewModel(
    ajax : IAjax,
    entrypoints : Entrypoints,
    portcall : PortCall
) {
    const
        client = ajax.Url<CancelPortCallRequest, {}>(entrypoints.CancelPortCall({ Id: portcall.Id() }), { useCache: true }),
        Req = new CancelPortCallRequest(),
        Show = S.data(false),
        Working = S.data(false),
        Success = S.data(false);

    return {
        Req,
        Show,
        Working,
        Success,
        Cancel
    };

    function Cancel() {
        Working(true);
        client.Post(Req).then(() => Success(true)).finally(() => Working(false));
    }
}

function ItineraryPointViewModel(pt : ItineraryPoint, ajax : IAjax) {
    // TH: We'll probably move the logic for editing ItineraryPoint times to an app endpoint that takes the entire PortCall itinerary.
    // In which case we probably won't hit the ApiItineraryPointController.
    // But for 12/31/15, this gives users a way of editing the SEA itinerary point.
    var client = ajax.Url<ItineraryPoint>(() => pt.Url()!, { useCache : true }),
        editables = ['Arrival.Estimated', 'Arrival.Actual', 'Departure.Estimated', 'Departure.Actual'],
        draft = AutoSavingDraftObject(pt, client, editables),
        Validation = ValidationWatcher(client);

    draft.Arrival.Effective = EffectiveDate(draft.Arrival);
    draft.Departure.Effective = EffectiveDate(draft.Departure);

    return Object.assign(objectCreate(draft), {
        Validation
    });
}

export type ReservationViewModel = ReturnType<typeof ReservationViewModel>;
function ReservationViewModel(
    res : BerthReservation,
    portcall : PortCallViewModel,
    ajax: IAjax,
    data: PCCache
) {
    const
        client = ajax.Url<BerthReservation>(() => res.Url() || '', { useCache : true }),
        // TODO: Sort out what of the footmark and side to stuff is staying here and what moves into
        // the new in port ops viewmodel. At least some probably stays here b/c in port ops needs to
        // support both new entities (add portcall) and existing entities (here).
        //
        // Distinguish different "tiers" of draft objects...
        // ...starting with Length.Num in a tier all by itself, b/c it doesn't need to get
        // saved (the server applies its own logic for computing it), but we still wanna
        // distinguish the draft value from the persisted value...
        draftForLength = DraftObject(res, [
            'Length.Num'
        ]),
        // ...and then those properties that auto-save.
        draft = DraftObject(draftForLength.object, [
            'Footmark.Num',
            'SideTo'
        ]),
        Validation = ValidationWatcher(client);

    // Wire up our auto-saver.
    DraftAutoSaver(draft, client);

    // Keep reservation length up-to-date when changing vessel or side to. This is purely for
    // client-side purposes (fast feedback when making changes) -- the server applies its own
    // version of the same logic.
    S(isInitialRun => {
        // Propagate portcall changes to non-cancelled reservations only, but update for SideTo
        // changes whether or not the reservation is cancelled.
        if (draft.object.CancelledDate() === null || draft.object.SideTo() !== res.SideTo()) {
            const newLength = BerthReservation.calculateLength(
                portcall.Vessel()?.Length ?? null,
                portcall.Vessel()?.Beam ?? null,
                portcall.LengthOnlyLength,
                draft.object.SideTo(),
                res.Length.UOM()
            );
            // Apply new length so long as we computed a usable value, but only if this is a
            // follow up run of this computation (due to data changing). Otherwise, leave
            // existing length as is whether it agrees w/ our computed length or not.
            if (newLength?.Num() && !isInitialRun) draft.object.Length.set(newLength as Measurement);
        }
        return false;
    }, true);

    // Revert draft Length.Num changes when we've persisted all pending changes to this
    // reservation and to our PortCall, as at that point the server should have calculated the
    // new length itself and we should have applied that value to our data cache.
    S(() => {
        if (draftForLength.methods.isCaching() && !draft.methods.isCaching() && !portcall.IsCaching()) {
            draftForLength.methods.checkpoint().revert();
        }
    });

    return {
        Model: draft.object,
        Validation
    };
}

function TopicCardViewModel(
    card : TopicCard,
    pcId : number,
    ajax : IAjax,
    topicCardListItemModel : (params : TopicCardListItemParams) => TopicCardListItem,
    serverTopicCardIdx : UniqueIndex<PortCallProfileTopicCard>,
    clock : { now : () => TZDate }
) {
    const serverTopicCard = serverTopicCardIdx(card.Id),
        canConfirm = () => serverTopicCard() ? serverTopicCard()!.CanConfirm() : false,
        client = ajax.Url<TopicCard>(() => card.Url() || '', { useCache: true }),
        draft = AutoSavingDraftObject(card, client, [
            // TH 12/08/2016 -- Workaround. When I didn't explicitly set props,
            // the draft created a draft signal for card.Comments, which caused
            // its runtime type to change from an ArrayDataSignal to a plain function,
            // which caused TopicCardListItem to throw at runtime.
            // This is just a general typing issue w/ draft objects and child collections.
            'ConfirmedDate',
            'DeniedDate'
        ]),
        listItem = topicCardListItemModel({ draft, canConfirm, clock });

    return {
        Model: draft,
        ListItem: listItem,
        GoToCard: () => window.location.hash = `#!topiccards?id=${pcId}&cardId=${card.Id()}`
    };
}

export type IssuesViewModel = ReturnType<typeof IssuesViewModel>;
function IssuesViewModel(Issues : () => Issue[]) {
    const Show = S.data(false);
    return { Show, Issues };
}

export type ReserveByLengthViewModel = ReturnType<typeof ReserveByLengthViewModel>;
function ReserveByLengthViewModel(portcall: PortCall) {
    const Show = S.data(false),
        newVesselLength = new Measurement({ Num: S.sample(portcall.LengthOnlyLength.Num) ?? 0, UOM: S.sample(portcall.LengthOnlyLength.UOM) ?? UOM.Foot }),
        newVesselUom = S.data(S.sample(newVesselLength.UOM)),
        ToggleableNewVesselLength = ToggleableMeasurement({ backingMeasurement: newVesselLength as any, uom: newVesselUom, conversions: lengthConversions }),
        ToggleableAirDraft = ToggleableMeasurement({ backingMeasurement: portcall.AirDraft, uom: newVesselUom, conversions: lengthConversions }),
        ToggleableDeepDraft = ToggleableMeasurement({ backingMeasurement: portcall.DeepDraft, uom: newVesselUom, conversions: lengthConversions });

    return {
        Show,
        ToggleableNewVesselLength,
        ToggleableAirDraft,
        ToggleableDeepDraft,
        NewVesselIsFeet: function (val? : boolean) {
            if (val === undefined) return newVesselUom() === UOM.Foot;
            else {
                var uom = val ? UOM.Foot : UOM.Meter;
                newVesselUom(uom);
                return val;
            }
        },
        SaveLength: SaveLength,
        //VesselImageUrl: content.VesselImageUrl,
        PortCallDrafts: portcall
    };

    function SaveLength() {
        S.freeze(() => {
            portcall.VesselId(null);
            portcall.LengthOnlyLength.set(newVesselLength as any);
            Show(false);
        });
    }
}

export type ExternalVesselSearchViewModel = ReturnType<typeof ExternalVesselSearchViewModel>;
function ExternalVesselSearchViewModel(
    ajax: IAjax,
    entrypoints: Entrypoints,
    data : PCCache,
    newVesselModel : (params : NewVesselParams) => NewVessel,
    PortCall : PortCall,
    onDismiss : () => void
) {
    const
        NewVessel = newVesselModel({
            lengthUOM: () => PortCall.LengthOnlyLength.UOM() ?? UOM.Foot,
            depthUOM: () => PortCall.DeepDraft.UOM() ?? UOM.Foot,
            onSelect: v => PortCall.VesselId(v.Id()),
            onDismiss
        }),
        ShowSave = () => NewVessel.Mode() != NewVesselMode.ExternalVesselService,
        saving = S.value(false);

    return {
        NewVessel,
        Cancel: onDismiss,
        Save,
        Saving: () => saving(),
        ShowSave
    };

    function Save() {
        if (saving()) return;
        saving(true);

        var request = NewVessel.CreateRequest();

        let req = ajax.Url<AddVesselRequest, VesselData>(entrypoints.AddVessel, { include: [ r => r.OverrideServiceVessel ] }).Post(request);
        req.then(json => S.freeze(() => {
            data.Vessels.update1(req.Date, json);
            PortCall.VesselId(json.Id);
            onDismiss();
        })).finally(() => saving(false));
    }
}

export type VesselViewModel = ReturnType<typeof VesselViewModel>;
function VesselViewModel(vessel : Vessel, ajax: IAjax, PortCall : PortCallViewModel, toggleableUom : () => string) {
    var client = ajax.Url<Vessel>(() => vessel.Url()!),
        editables = [
            'DOS.ExpirationDate',
            'DOS.EffectiveDate',
            'DOS.IssuedBy'
        ],
        draft = AutoSavingDraftObject(vessel, client, editables),
        validation = ValidationWatcher(client);

    var toggleableLength = ToggleableMeasurement({ backingMeasurement: draft.Length, uom: toggleableUom, conversions: lengthConversions }),
        toggleableBeam = ToggleableMeasurement({ backingMeasurement: draft.Beam, uom: toggleableUom, conversions: lengthConversions });

    return Object.assign(objectCreate(draft), {
        ToggleableLength: toggleableLength,
        ToggleableBeam: toggleableBeam,
        Validation: validation
    });
}

export type DOSViewModel = ReturnType<typeof DOSViewModel>;
function DOSViewModel(vesselDraft : () => VesselViewModel | null, rights : () => PortCallRights) {
    var vessel = vesselDraft();
    if (vessel === null) return null;
    return Object.assign(objectCreate(vessel.DOS), {
        Rights: rights
    });
}

export type ManageCoordinationsViewModel = ReturnType<typeof ManageCoordinationsViewModel>;
function ManageCoordinationsViewModel(
    ajax : IAjax,
    entrypoints : Entrypoints,
    addCCardsReqCoordSelStorage : (params : AddCCardsRequestCoordinationSelectionStorageParams) => CoordinationSelectionStorageFactory,
    selectCoordinationsModel : (params : SelectCoordinationsParams) => SelectCoordinations,
    PortCall : PortCall,
    onDismiss : () => void
) {
    const
        url = () => entrypoints.AddCoordinationCards({ Id: PortCall.Id() }),
        client = ajax.Url<AddCoordinationCardsRequest, unknown>(url, {
            useCache: true,
            include: [ r => r.RequestedCards ]
        }),
        Request = new AddCoordinationCardsRequest(),
        SelectCoordinations = selectCoordinationsModel({
            PortCall,
            SelectionStorage: addCCardsReqCoordSelStorage({ Request })
        }),
        Submitting = S.value(false);

    return {
        SelectCoordinations,
        Submitting,
        CanSubmit,
        Submit,
        Cancel: onDismiss
    };

    function CanSubmit() {
        return Request.RequestedCards().length > 0 && SelectCoordinations.IsValidForSubmit();
    }

    function Submit() {
        if (Submitting()) return;
        Submitting(true);
        client.Post(Request).then(onDismiss).finally(() => Submitting(false));
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/portcallprofile/model.ts